import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';

import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';
import DatePicker from '../components/DatePicker';
import Select from '../components/Select';
import Radio from '../components/Radio';

import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';
import useMainContext from '../hooks/useMainContext';

import { maxWidth } from '../components/configs';

import tries from '../api/tries';

import { 
	tryIsNewClientOptions, 
	tryRecruitmentOptions 
} from '../configs/tries';

const dateFormat = 'YYYY-MM-DD';
const queryParamKey = 'update-tracking';

const generateDefaultState = () => {
	return {
		name: '',
		isNewClient: tryIsNewClientOptions[0].value,
		tryDate: moment().format(dateFormat),
		followUpDate1: null,
		followUpDate2: null,
		followUpDate3: null,
		followUpDate4: null,
		followUpDate5: null,
		followUpDate6: null,
		followUpDate7: null,
		ANP: '',
		remark: '',
		recruitment: tryRecruitmentOptions[0].value,
	}
};

const useStyles = makeStyles(theme => ({
	content: {
		maxWidth,
		margin: 'auto',
		width: '100%',
		backgroundColor: '#fff',
		padding: 10,
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column'
	},
	loading: {
		marginTop: 50,
		marginBottom: 50,
		color: '#c7c7c7'
	},
	submitButton: {
		width: '100%',
		marginTop: 20,
		marginBottom: 20,
		color: '#fff',
		borderRadius: 6,
		fontWeight: 600,
		backgroundColor: theme.palette.success.main,
		'&:hover': {
			backgroundColor: theme.palette.success.dark,
		}
	}
}));

function UpdateTryDialog(props) {
	const classes = useStyles();
	const handleSnackbarChange = useSnackbarText();
	const [queryParams, setQueryParams] = useQueryParams();
	const [state, _setState] = useState(generateDefaultState);
	const [error, setError] = useState({});
	const [disabled, setDisable] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const { onTryClose } = useMainContext();

	const queryValue = queryParams[queryParamKey];

	const setState = useCallback((newState = {}) => {
		_setState((prevState) => ({
			...prevState,
			...newState,
		}));
	}, []);

	const handleValueCheck = () => {
		let noError = true;
		let error = {};
		if(!state.name) {
			noError = false;
			error.name = 'Invalid value.';
		}
		setError(error);
		return noError;
	};

	const handleChange = (type) => (event, value) => {
		if(value !== undefined) setState({ [type]: value });
		else setState({ [type]: event.target.value });
	};

	const handleSubmit = async () => {
		if(!handleValueCheck()) {
			handleSnackbarChange({ text: 'Invalid inputs', variant: 'error' });
			return window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
		};

		setDisable(true);
		let { data, success } = await tries.updateTry({
			...state,
			tryID: queryValue,
		});
		setDisable(false);
		if(success) {
			handleSnackbarChange({ text: 'Prospect tracking updated', variant: 'success' });
		} else {
			return handleSnackbarChange({ text: data.message, variant: 'error' });
		}

		if(onTryClose) onTryClose({ ...state, tryID: parseInt(queryValue) });
		setQueryParams({ 'update-tracking': null });
	};

	useEffect(() => {
		_setState(generateDefaultState());
	}, [queryValue]);

	useEffect(() => {
		const tryID = queryValue;
		const getData = async () => {
			let { data, success } = await tries.getTry({ tryID });
			if(success) {
				setState({
					...data,
					isNewClient: String(data.isNewClient),
					recruitment: String(data.recruitment),
				});
				setLoaded(true);
			} else {
				return handleSnackbarChange({ text: data.message, variant: 'error' });
			}
		}

		if(tryID) getData();
	}, [queryValue, handleSnackbarChange, setState, setLoaded]);

	return (
		<FullScreenDialog 
			title="Update Prospect Tracking"
			queryParamKey={queryParamKey}
		>
			{!loaded && <div className={classes.loading}>loading</div>}
			{loaded && (
				<div className={classes.content}>
					<TextField 
						label="Client Name*"
						value={state.name}
						onChange={handleChange('name')}
						error={Boolean(error.name)}
						helperText={error.name}
					/>

					<Radio 
						label="Client"
						options={tryIsNewClientOptions}
						value={state.isNewClient}
						onChange={handleChange('isNewClient')}
						row
					/>

					<DatePicker
						label="Date Opened"
						value={state.tryDate}
						onChange={handleChange('tryDate')}
					/>

					<DatePicker
						label="Follow Up Date 1st"
						value={state.followUpDate1}
						onChange={handleChange('followUpDate1')}
						clearable
					/>

					<DatePicker
						label="Follow Up Date 2nd"
						value={state.followUpDate2}
						onChange={handleChange('followUpDate2')}
						clearable
					/>

					<DatePicker
						label="Follow Up Date 3rd"
						value={state.followUpDate3}
						onChange={handleChange('followUpDate3')}
						clearable
					/>

					<DatePicker
						label="Follow Up Date 4th"
						value={state.followUpDate4}
						onChange={handleChange('followUpDate4')}
						clearable
					/>

					<DatePicker
						label="Follow Up Date 5th"
						value={state.followUpDate5}
						onChange={handleChange('followUpDate5')}
						clearable
					/>

					<DatePicker
						label="Follow Up Date 6th"
						value={state.followUpDate6}
						onChange={handleChange('followUpDate6')}
						clearable
					/>

					<DatePicker
						label="Follow Up Date 7th"
						value={state.followUpDate7}
						onChange={handleChange('followUpDate7')}
						clearable
					/>

					<TextField 
						label="ANP"
						value={state.ANP}
						onChange={handleChange('ANP')}
						type="number"
					/>

					<TextField 
						label="Remarks"
						value={state.remark}
						onChange={handleChange('remark')}
					/>

					<Select 
						label="Recruitment"
						options={tryRecruitmentOptions}
						value={state.recruitment}
						onChange={handleChange('recruitment')}
					/>

					<Button
						className={classes.submitButton}
						disabled={disabled}
						variant="contained"
						color="primary"
						onClick={handleSubmit}
					>
						Update
					</Button>
				</div>
			)}
		</FullScreenDialog>
	);
}

export default UpdateTryDialog;
